import styled from '@emotion/styled';
import type { JSX, ReactNode } from 'react';

export interface PageProps {
  title: string;
  description?: string;
  children?: ReactNode;
}

/**
 * Standard page frame: one `<h1>`, an optional lead paragraph, then content.
 *
 * Every page going through this is what keeps the heading hierarchy correct — a
 * document with two `<h1>`s, or none, makes the page outline useless for anyone
 * navigating by heading.
 *
 * Reusable surfaces (`Card`, `DetailList`) live in `./surfaces.ts`.
 */
export function Page({ title, description, children }: PageProps): JSX.Element {
  return (
    <Wrapper>
      <h1>{title}</h1>
      {description ? <Lead>{description}</Lead> : null}
      {children}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  max-width: ${({ theme }) => theme.layout.contentMaxWidth};

  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const Lead = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
`;
